/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';

import ErrorName from '../../error/error-name';
import FileSystemPathFactory from './file-system-path-factory';
import PromiseUtil from '../../promise/promise-util';

/**
 * @import { FileSystemEntry, FileSystemManager, FileSystemName, FileSystemUnit } from '.'
 * @import FileSystemUnitFinder from '../../file-system-unit-finder/file-system-unit-finder'
 * @import { Logger } from '../../logger/logger'
 * @import { LogicalUnitMapper } from '../../logical-unit-mapper'
 * @import LogicalUnit from '../../logical-unit/logical-unit'
 * @import Store from '../../store/store'
 * @import FileSystemPath from './file-system-path'
 */

export default class FileSystemUtil {
  /**
   * Nalezne jednotku k uložení obsahu.
   * Zatím vždy vybere interní jednotku, u které očekává, že bude jediná přítomná tohoto typu.
   * Dočasná náhrada před implementací pokročilejšího výběru vhodných jednotek.
   * @param {FileSystemManager} fileSystemManager
   * @param {FileSystemUnitFinder} fileSystemUnitFinder
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {string} storageString
   * @returns {Promise<LogicalUnit | null>} Nalezená primární jednotka.
   */
  static async find(
    fileSystemManager,
    fileSystemUnitFinder,
    logicalUnitMapper,
    storageString
  ) {
    // Získá všechny dostupné jednotky.
    const units = await fileSystemManager.getAllUnits();
    // Vyfiltruje jednotky dostupné pro uložení.
    const unit = await fileSystemUnitFinder.find(units, storageString);
    if (isNil(unit)) {
      return null;
    }

    // Provede mapování fyzických jednotek na logické.
    return logicalUnitMapper.toLogical(unit);
  }

  /**
   * Vrátí seznam logických jednotek, na kterých byly uloženy nějaké assety.
   * Výsledný seznam je seřazený podle abecedy.
   * @param {FileSystemManager} fileSystemManager
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {Store} store
   * @returns {Promise<LogicalUnit[]>}
   */
  static async findUsedLogicalUnits(
    fileSystemManager,
    logicalUnitMapper,
    store
  ) {
    const usedLogicalUnitNames = await getLogicalUnitsFromAssets(store);
    let logicalUnits = await PromiseUtil.allParallel(
      (
        await fileSystemManager.getAllUnits()
      ).map((unit) => () => logicalUnitMapper.toLogical(unit))
    );
    logicalUnits = logicalUnits.filter((unit) =>
      usedLogicalUnitNames.has(unit.getName())
    );
    logicalUnits.sort(sortLogicalUnits);
    return logicalUnits;
  }

  /**
   * Vylistuje obsah adresáře. Pokud adresář neexistuje, vrátí prázdný seznam. Pokud cesta není adresář, vyhodí výjimku.
   * Výsldek je sežadný podle abecedy, přičemž adresáře jsou před soubory.
   * @param {FileSystemManager} fileSystemManager
   * @param {FileSystemPath} basePhysicalPath
   * @returns {Promise<FileSystemEntry[]>}
   */
  static async listDirectory(fileSystemManager, basePhysicalPath) {
    try {
      const directory = await fileSystemManager.getDirectory(basePhysicalPath);
      return (await directory.list()).sort(sortFileSystemEntries);
    } catch (error) {
      // Pokud složka s workspace neexistuje, považuje se za prázdnou.
      if (/** @type {Error} */ (error).name === ErrorName.NotFoundError) {
        return [];
      }
      throw error;
    }
  }
}

/**
 * Získá množinu logických jednotek, na kterých byly uloženy nějaké assety.
 * @param {Store} store
 * @returns {Promise<Set<FileSystemName>>}
 */
export async function getLogicalUnitsFromAssets(store) {
  const assets = await store.findAllAsset();
  return new Set(
    assets
      .map((asset) => FileSystemPathFactory.fromValue(asset.path).getUnit())
      .filter((unit) => unit !== '')
  );
}

/**
 * @param {FileSystemEntry} a
 * @param {FileSystemEntry} b
 * @returns {number}
 */
function sortFileSystemEntries(a, b) {
  if (a.isDirectory() && !b.isDirectory()) {
    return -1;
  }
  if (!a.isDirectory() && b.isDirectory()) {
    return 1;
  }
  return a.getName().localeCompare(b.getName());
}

/**
 * @param {LogicalUnit} a
 * @param {LogicalUnit} b
 * @returns {number}
 */
function sortLogicalUnits(a, b) {
  return a.getName().localeCompare(b.getName());
}

export const internal = { sortFileSystemEntries, sortLogicalUnits };
